import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Plus, Download, Upload, FileSpreadsheet, ClipboardPaste, Trash2,
  Pencil, Paperclip, FileText, X, ArrowRight, AlertTriangle, PaintBucket, RotateCcw, Filter, Wrench,
} from "lucide-react";
import { AppLayout, PageHeader } from "@/components/AppLayout";
import { DataTable, Toolbar, SearchInput, Btn, Field, Input, Select, Textarea, Modal, LengthInput } from "@/components/Primitives";
import { StatusBadge } from "@/components/StatusBadge";
import {
  api, fmtNumber, fmtDate, formatApiErrorDetail, calcCbm, calcWeight, saveBlob,
  ORIGINS, COLOURS, LENGTHS, PRODUCT_TYPES, API, can,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// On the Receiving page, a finished/processed receipt line reaching "available"
// stock means the receiving step is done — show it as "Completed" to avoid
// confusion with stock-on-hand availability.
const RECEIVING_STATUS_LABELS = { available: "Completed" };

// Debounced duplicate-weighbridge warning shown under the Weighbridge Number input.
function WbDupWarning({ value, excludeId }) {
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    const wb = (value || "").trim();
    if (!wb) { setMsg(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/receiving/check-duplicate", { params: { weighbridge_number: wb, exclude_id: excludeId || undefined } });
        if (r.data.duplicate) {
          const m = r.data.matches?.[0];
          const text = `Duplicate! Weighbridge # "${wb}" already captured${m?.delivery_note_number ? ` on DN ${m.delivery_note_number}` : ""}${m?.date_received ? ` (${m.date_received})` : ""}`;
          setMsg(text);
          toast.warning(text);
        } else setMsg(null);
      } catch { setMsg(null); }
    }, 600);
    return () => clearTimeout(t);
  }, [value, excludeId]);
  if (!msg) return null;
  return (
    <div className="text-xs text-red-600 font-semibold mt-1 flex items-center gap-1" data-testid="wb-dup-warning">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {msg}
    </div>
  );
}

const rawEmpty = {
  date_received: new Date().toISOString().slice(0, 10),
  product_type: "Poles", product_id: "", product_name: "",
  delivery_note_number: "", weighbridge_number: "",
  origin: "EIT", transporter: "", truck_registration: "",
  quantity: 0, weight: 0, avg_pole_weight: "",
  supplier_id: "", supplier_name: "", comments: "",
  requires_processing: true, is_damaged: false,
  processed_by_us: true, processed_by_us_qty: "",
};

const finishedEmpty = {
  date_received: new Date().toISOString().slice(0, 10),
  product_type: "Poles", product_id: "", product_name: "",
  delivery_note_number: "", order_number: "", export_destination: "",
  bundle_number: "", article_number: "",
  weighbridge_number: "",
  origin: "EIT", transporter: "", truck_registration: "",
  butt_size: "", thick: "", wide: "", length: "",
  quantity: 0, colour: "Silver", weight: 0,
  supplier_id: "", supplier_name: "",
  requires_processing: false, is_damaged: false, comments: "",
  processed_by_us: false, processed_by_us_qty: "",
};

// A "receipt" groups all receiving lines that share a Delivery Note + date.
const receiptKey = (r) => `${r.delivery_note_number || "—"}__${r.date_received || "—"}`;

// RAW parent receipts keep their original quantity while processed child rows are
// added per pole — count only the remaining (unprocessed) qty to avoid double-counting.
const effQty = (r) => {
  const q = Number(r.quantity) || 0;
  return r.requires_processing ? Math.max(q - (Number(r.processed_quantity) || 0), 0) : q;
};
const effWeight = (r) => {
  const w = Number(r.weight) || 0;
  if (!r.requires_processing || !r.processed_quantity) return w;
  const apw = Number(r.avg_pole_weight) || (w / ((Number(r.quantity) || 0) || 1));
  return Math.round(apw * effQty(r) * 100) / 100;
};

export default function Receiving() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [damageStats, setDamageStats] = useState({}); // receiptKey -> {damaged, salvaged, notSalvaged}
  const [q, setQ] = useState("");
  const [mode, setMode] = useState(null); // 'raw' | 'finished' | null
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("receipts"); // 'receipts' breakdown | 'lines' all-lines
  const [receiptSel, setReceiptSel] = useState(null); // selected receipt key
  const [receiptDocCounts, setReceiptDocCounts] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkPBU, setBulkPBU] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const advEmpty = {
    origin: "", product_type: "", colour: "", supplier: "", destination: "",
    order_number: "", dn: "", buttMin: "", buttMax: "", lenMin: "", lenMax: "",
    dateFrom: "", dateTo: "",
  };
  const [adv, setAdv] = useState(advEmpty);
  const canCreate = can(user?.role, "receiving:create");
  const canDelete = can(user?.role, "receiving:delete");
  const canUpdate = can(user?.role, "receiving:update");

  const load = async () => {
    const r = await api.get("/receiving");
    setItems(r.data);
    setSelected(new Set());
    try {
      const dmg = await api.get("/damages");
      const m = {};
      (dmg.data || []).forEach((d) => {
        const k = `${d.delivery_note_number || "—"}__${d.date_received || "—"}`;
        const e = m[k] || (m[k] = { damaged: 0, salvaged: 0, notSalvaged: 0 });
        const qd = Number(d.quantity_damaged) || 0;
        e.damaged += qd;
        if (d.status === "salvaged") e.salvaged += qd;
        else if (d.status === "write_off") e.notSalvaged += qd;
      });
      setDamageStats(m);
    } catch { /* ignore */ }
    try {
      const dr = await api.get("/documents", { params: { linked_to: "receipt" } });
      const counts = {};
      dr.data.forEach((d) => { counts[d.linked_id] = (counts[d.linked_id] || 0) + 1; });
      setReceiptDocCounts(counts);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    // eslint-disable-next-line
    load();
  }, []);

  const distinct = (key) => Array.from(new Set(items.map((r) => r[key]).filter((v) => v != null && v !== ""))).sort();

  const filtered = items.filter((x) => {
    if (statusFilter && x.status !== statusFilter) return false;
    if (modeFilter === "raw" && !x.requires_processing) return false;
    if (modeFilter === "fin" && (x.requires_processing || x.source_receiving_id)) return false;
    if (modeFilter === "proc" && !x.source_receiving_id) return false;
    // advanced multi-column filters
    if (adv.origin && x.origin !== adv.origin) return false;
    if (adv.product_type && x.product_type !== adv.product_type) return false;
    if (adv.colour && x.colour !== adv.colour) return false;
    if (adv.supplier && x.supplier_name !== adv.supplier) return false;
    if (adv.destination && !String(x.export_destination || "").toLowerCase().includes(adv.destination.toLowerCase())) return false;
    if (adv.order_number && !String(x.order_number || "").toLowerCase().includes(adv.order_number.toLowerCase())) return false;
    if (adv.dn && !String(x.delivery_note_number || "").toLowerCase().includes(adv.dn.toLowerCase())) return false;
    if (adv.buttMin !== "" && (x.butt_size == null || Number(x.butt_size) < Number(adv.buttMin))) return false;
    if (adv.buttMax !== "" && (x.butt_size == null || Number(x.butt_size) > Number(adv.buttMax))) return false;
    if (adv.lenMin !== "" && (x.length == null || Number(x.length) < Number(adv.lenMin))) return false;
    if (adv.lenMax !== "" && (x.length == null || Number(x.length) > Number(adv.lenMax))) return false;
    if (adv.dateFrom && (x.date_received || "") < adv.dateFrom) return false;
    if (adv.dateTo && (x.date_received || "") > adv.dateTo) return false;
    if (!q) return true;
    return [
      x.delivery_note_number, x.supplier_name, x.product_name, x.id,
      x.transporter, x.order_number, x.bundle_number, x.article_number,
      x.colour, x.origin, x.weighbridge_number,
    ].some((v) => v && String(v).toLowerCase().includes(q.toLowerCase()));
  });

  const activeFilterCount =
    (modeFilter ? 1 : 0) + (statusFilter ? 1 : 0) +
    Object.values(adv).filter((v) => v !== "").length;

  const clearFilters = () => {
    setAdv(advEmpty); setModeFilter(""); setStatusFilter(""); setQ("");
  };

  const exportFiltered = async () => {
    try {
      const res = await api.post("/exports/receiving-selected.xlsx",
        { ids: filtered.map((r) => r.id) }, { responseType: "blob" });
      saveBlob(res, "receiving-filtered.xlsx");
      toast.success(`Exported ${filtered.length} filtered line(s)`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const filteredIds = filtered.map((r) => r.id);
  // Render is paged; ALL lines stay loaded so filters, select-all, exports and totals cover everything.
  const PAGE_SIZE = 200;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [q, statusFilter, modeFilter, adv, view]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const LinesPager = () => pageCount > 1 && (
    <div className="flex items-center justify-end gap-3 text-xs my-2" data-testid="receiving-pager">
      <span className="text-stone-500 font-mono" data-testid="receiving-pager-info">
        Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length} lines — filters, exports and select-all cover everything
      </span>
      <Btn size="sm" variant="secondary" onClick={() => setPage(Math.max(1, safePage - 1))} disabled={safePage <= 1} testid="receiving-prev-page">‹ Prev</Btn>
      <span className="font-mono">{safePage} / {pageCount}</span>
      <Btn size="sm" variant="secondary" onClick={() => setPage(Math.min(pageCount, safePage + 1))} disabled={safePage >= pageCount} testid="receiving-next-page">Next ›</Btn>
    </div>
  );
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const someSelected = filteredIds.some((id) => selected.has(id));
  const toggleAll = () => {
    if (allSelected) {
      const next = new Set(selected);
      filteredIds.forEach((id) => next.delete(id));
      setSelected(next);
    } else {
      const next = new Set(selected);
      filteredIds.forEach((id) => next.add(id));
      setSelected(next);
    }
  };
  const toggleRow = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };
  const selectedItems = items.filter((r) => selected.has(r.id));

  const distinctStatuses = Array.from(new Set(items.map((r) => r.status))).sort();

  const receipts = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      const k = receiptKey(r);
      if (!map.has(k)) map.set(k, {
        key: k, dn: r.delivery_note_number || "—", date: r.date_received || "—",
        lines: [], origins: new Set(), suppliers: new Set(), modes: new Set(), statuses: new Set(),
        qty: 0, damaged: 0, weight: 0, cbm: 0,
      });
      const g = map.get(k);
      g.lines.push(r);
      if (r.origin) g.origins.add(r.origin);
      if (r.supplier_name) g.suppliers.add(r.supplier_name);
      g.modes.add(r.requires_processing ? "RAW" : r.source_receiving_id ? "PROC" : "FIN");
      if (r.status) g.statuses.add(r.status);
      g.qty += effQty(r);
      if (r.status === "damaged" || r.is_damaged) g.damaged += Number(r.quantity) || 0;
      g.weight += effWeight(r);
      g.cbm += calcCbm(r.butt_size, r.length, effQty(r));
    });
    return Array.from(map.values())
      .map((g) => {
        const ds = damageStats[g.key] || { damaged: 0, salvaged: 0, notSalvaged: 0 };
        return { ...g, damaged: ds.damaged, salvaged: ds.salvaged, notSalvaged: ds.notSalvaged };
      })
      .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  }, [filtered, damageStats]);

  const selectedReceipt = receiptSel
    ? { key: receiptSel, lines: items.filter((r) => receiptKey(r) === receiptSel) }
    : null;

  const onDelete = async (r) => {
    if (!confirm(`Delete receipt ${r.delivery_note_number || r.id.slice(0, 8)}? This will also remove the linked stock bundle.`)) return;
    try {
      await api.delete(`/receiving/${r.id}`);
      toast.success("Receipt deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const [damageTarget, setDamageTarget] = useState(null);
  const [damageReason, setDamageReason] = useState("");
  const [damageSalvageable, setDamageSalvageable] = useState("yes");
  const canDamage = can(user?.role, "damages:create");
  const isDamageable = (r) => ["available", "allocated", "processed"].includes(r.status);

  const doMarkDamaged = async () => {
    try {
      await api.post(`/receiving/${damageTarget.id}/mark-damaged`, {
        reason: damageReason || null,
        salvageable: damageSalvageable === "yes",
      });
      toast.success(damageSalvageable === "yes"
        ? "Marked damaged — now in Processing → Awaiting Salvage"
        : "Written off — not salvageable");
      setDamageTarget(null);
      setDamageReason("");
      setDamageSalvageable("yes");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const markPaintedLine = async (r) => {
    try {
      await api.post("/processing/mark-painted", { receiving_ids: [r.id] });
      toast.success("Marked painted — line is now Completed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const revertLine = async (r) => {
    if (!window.confirm(`Send this pole (DN ${r.delivery_note_number || "—"}) back to Processing? The original receipt quantity will be restored so you can re-process it.`)) return;
    try {
      await api.post("/processing/revert", { receiving_ids: [r.id] });
      toast.success("Sent back to Processing — source quantity restored");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <AppLayout>
      <PageHeader
        title="Stock Receiving"
        subtitle="Capture incoming truckloads. Raw poles need processing; finished poles go straight to stock or auto-allocate to an order."
        actions={
          <>
            <Btn variant="secondary" as="a" href={`${API}/exports/receiving.xlsx`} target="_blank" testid="export-receiving-xlsx">
              <Download className="h-4 w-4" /> Export
            </Btn>
            {canCreate && (
              <>
                <Btn variant="secondary" onClick={() => setMode("raw")} testid="new-raw-btn">
                  <Plus className="h-4 w-4" /> Raw (to process)
                </Btn>
                <Btn onClick={() => setMode("finished")} testid="new-finished-btn">
                  <Plus className="h-4 w-4" /> Finished (ready)
                </Btn>
              </>
            )}
          </>
        }
      />

      <Toolbar>
        <div className="inline-flex rounded-sm border border-stone-300 overflow-hidden" data-testid="view-toggle">
          <button onClick={() => setView("receipts")} className={`px-3 py-2 text-sm font-medium transition-colors ${view === "receipts" ? "bg-emerald-900 text-white" : "bg-white text-stone-700 hover:bg-stone-50"}`} data-testid="view-receipts">Receipts</button>
          <button onClick={() => setView("lines")} className={`px-3 py-2 text-sm font-medium transition-colors ${view === "lines" ? "bg-emerald-900 text-white" : "bg-white text-stone-700 hover:bg-stone-50"}`} data-testid="view-lines">All Lines</button>
        </div>
        <SearchInput value={q} onChange={setQ} placeholder="Search DN, supplier, order, bundle, article..." testid="receiving-search" />
        <Select value={modeFilter} onChange={(e) => setModeFilter(e.target.value)} className="!w-32" data-testid="filter-mode">
          <option value="">All modes</option>
          <option value="raw">RAW (to process)</option>
          <option value="fin">FIN (direct)</option>
          <option value="proc">PROC (from processing)</option>
        </Select>
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="!w-40" data-testid="filter-status">
          <option value="">All statuses</option>
          {distinctStatuses.map((s) => <option key={s} value={s}>{RECEIVING_STATUS_LABELS[s] || s}</option>)}
        </Select>
        <Btn variant="secondary" onClick={() => setShowFilters((v) => !v)} testid="toggle-filters">
          <Filter className="h-4 w-4" /> Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
        </Btn>
        <div className="text-xs text-stone-500 ml-auto" data-testid="receiving-count">
          {view === "receipts" ? `${receipts.length} receipts · ${filtered.length} lines` : `${filtered.length} records`}
        </div>
        {view === "receipts" && (
          <a href={`${API}/exports/receipts.xlsx`} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-sm border border-stone-300 bg-white text-stone-700 hover:bg-stone-50 transition-colors"
             data-testid="export-receipts-btn">
            <Download className="h-4 w-4" /> Export Breakdown
          </a>
        )}
        {view === "lines" && (
          <>
            <a href={`${API}/exports/receiving.xlsx`} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-sm border border-stone-300 bg-white text-stone-700 hover:bg-stone-50 transition-colors"
               data-testid="export-all-btn">
              <Download className="h-4 w-4" /> Export All Lines
            </a>
            <Btn variant="secondary" onClick={exportFiltered} testid="export-filtered-btn">
              <Download className="h-4 w-4" /> Export Filtered ({filtered.length})
            </Btn>
            {canUpdate && (
              <Btn variant="secondary" onClick={() => setBulkPBU(true)} testid="bulk-pbu-btn">
                <Wrench className="h-4 w-4" /> Processed By Us ({selected.size || filtered.length})
              </Btn>
            )}
          </>
        )}
      </Toolbar>

      {showFilters && (
        <div className="panel p-3 mb-3" data-testid="filter-panel">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <Field label="Origin">
              <Select value={adv.origin} onChange={(e) => setAdv({ ...adv, origin: e.target.value })} data-testid="adv-origin">
                <option value="">All</option>
                {distinct("origin").map((v) => <option key={v} value={v}>{v}</option>)}
              </Select>
            </Field>
            <Field label="Product Type">
              <Select value={adv.product_type} onChange={(e) => setAdv({ ...adv, product_type: e.target.value })} data-testid="adv-product-type">
                <option value="">All</option>
                {distinct("product_type").map((v) => <option key={v} value={v}>{v}</option>)}
              </Select>
            </Field>
            <Field label="Colour">
              <Select value={adv.colour} onChange={(e) => setAdv({ ...adv, colour: e.target.value })} data-testid="adv-colour">
                <option value="">All</option>
                {distinct("colour").map((v) => <option key={v} value={v}>{v}</option>)}
              </Select>
            </Field>
            <Field label="Supplier">
              <Select value={adv.supplier} onChange={(e) => setAdv({ ...adv, supplier: e.target.value })} data-testid="adv-supplier">
                <option value="">All</option>
                {distinct("supplier_name").map((v) => <option key={v} value={v}>{v}</option>)}
              </Select>
            </Field>
            <Field label="Order # contains">
              <Input value={adv.order_number} onChange={(e) => setAdv({ ...adv, order_number: e.target.value })} data-testid="adv-order" />
            </Field>
            <Field label="DN # contains">
              <Input value={adv.dn} onChange={(e) => setAdv({ ...adv, dn: e.target.value })} data-testid="adv-dn" />
            </Field>
            <Field label="Destination contains">
              <Input value={adv.destination} onChange={(e) => setAdv({ ...adv, destination: e.target.value })} data-testid="adv-destination" />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Butt min"><Input type="number" value={adv.buttMin} onChange={(e) => setAdv({ ...adv, buttMin: e.target.value })} data-testid="adv-butt-min" /></Field>
              <Field label="Butt max"><Input type="number" value={adv.buttMax} onChange={(e) => setAdv({ ...adv, buttMax: e.target.value })} data-testid="adv-butt-max" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Len min"><Input type="number" step="0.1" value={adv.lenMin} onChange={(e) => setAdv({ ...adv, lenMin: e.target.value })} data-testid="adv-len-min" /></Field>
              <Field label="Len max"><Input type="number" step="0.1" value={adv.lenMax} onChange={(e) => setAdv({ ...adv, lenMax: e.target.value })} data-testid="adv-len-max" /></Field>
            </div>
            <Field label="Date from"><Input type="date" value={adv.dateFrom} onChange={(e) => setAdv({ ...adv, dateFrom: e.target.value })} data-testid="adv-date-from" /></Field>
            <Field label="Date to"><Input type="date" value={adv.dateTo} onChange={(e) => setAdv({ ...adv, dateTo: e.target.value })} data-testid="adv-date-to" /></Field>
          </div>
          <div className="flex justify-end mt-2">
            <Btn variant="secondary" onClick={clearFilters} testid="clear-filters">
              <X className="h-4 w-4" /> Clear all filters
            </Btn>
          </div>
        </div>
      )}

      {view === "lines" && selected.size > 0 && (
        <div className="flex items-center gap-3 px-3 py-2 mb-3 bg-red-50 border border-red-200 rounded-sm" data-testid="bulk-bar">
          <span className="font-semibold text-red-900 text-sm" data-testid="bulk-count">
            {selected.size} selected
          </span>
          <button
            onClick={() => setSelected(new Set())}
            className="text-xs text-stone-600 hover:text-stone-900 underline"
            data-testid="bulk-clear"
          >Clear selection</button>
          <div className="ml-auto flex gap-2">
            <Btn variant="secondary" onClick={() => {
              const next = new Set(selected);
              filteredIds.forEach((id) => next.add(id));
              setSelected(next);
            }} testid="bulk-select-filtered">Select all {filtered.length} filtered</Btn>
            {canUpdate && (
              <Btn onClick={() => setShowBulkEdit(true)} testid="bulk-edit-btn">
                <Pencil className="h-4 w-4" /> Bulk Edit {selected.size}
              </Btn>
            )}
            {canDelete && (
              <Btn variant="danger" onClick={() => setShowBulkDelete(true)} testid="bulk-delete-btn">
                <Trash2 className="h-4 w-4" /> Delete {selected.size}
              </Btn>
            )}
          </div>
        </div>
      )}

      {view === "receipts" && (
        <DataTable
          testid="receipts-table"
          rowKey="key"
          onRowClick={(g) => setReceiptSel(g.key)}
          columns={[
            { header: "Date", mono: true, render: (g) => g.date },
            { header: "DN #", mono: true, render: (g) => <span className="font-semibold">{g.dn}</span> },
            { header: "Mode", render: (g) => (
              <div className="flex gap-1">
                {Array.from(g.modes).map((m) => (
                  <span key={m} className={`text-[10px] px-1.5 py-0.5 rounded-sm font-bold tracking-wider ${m === "RAW" ? "bg-amber-100 text-amber-800" : m === "PROC" ? "bg-emerald-100 text-emerald-900" : "bg-emerald-50 text-emerald-800"}`}>{m}</span>
                ))}
              </div>
            )},
            { header: "Origin", render: (g) => Array.from(g.origins).join(", ") || "—" },
            { header: "Total Qty", align: "right", mono: true, render: (g) => fmtNumber(g.qty) },
            { header: "Damaged Received", align: "right", mono: true, render: (g) => (
              g.damaged > 0
                ? <span className="text-red-700 font-semibold">{fmtNumber(g.damaged)}</span>
                : <span className="text-stone-400">0</span>
            )},
            { header: "Salvaged", align: "right", mono: true, render: (g) => (
              g.salvaged > 0
                ? <span className="text-emerald-700 font-semibold">{fmtNumber(g.salvaged)}</span>
                : <span className="text-stone-400">0</span>
            )},
            { header: "Not Salvaged", align: "right", mono: true, render: (g) => (
              g.notSalvaged > 0
                ? <span className="text-red-700 font-semibold">{fmtNumber(g.notSalvaged)}</span>
                : <span className="text-stone-400">0</span>
            )},
            { header: "Incoming Total Weight", align: "right", mono: true, render: (g) => fmtNumber(g.weight) + " kg" },
            { header: "Calculated Weight (kg)", align: "right", mono: true, render: (g) => fmtNumber(g.cbm * 900) },
            { header: "Status", render: (g) => (
              <div className="flex flex-wrap gap-1">{Array.from(g.statuses).map((s) => <StatusBadge key={s} status={s} labels={RECEIVING_STATUS_LABELS} />)}</div>
            )},
            { header: "Docs", align: "right", render: (g) => (
              <button onClick={(e) => { e.stopPropagation(); setReceiptSel(g.key); }}
                      className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-emerald-800" title="Documents" data-testid={`receipt-docs-${g.key}`}>
                <Paperclip className="h-3.5 w-3.5" />
                <span className="font-mono">{receiptDocCounts[g.key] || 0}</span>
              </button>
            )},
            { header: "", render: () => <ArrowRight className="h-4 w-4 text-stone-400" /> },
          ]}
          rows={receipts}
          empty="No receipts found."
        />
      )}

      {view === "lines" && <LinesPager />}
      {view === "lines" && (
      <DataTable
        testid="receiving-table"
        columns={[
          ...(canDelete ? [{
            header: (
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }}
                onChange={toggleAll}
                className="cursor-pointer"
                data-testid="bulk-select-all"
              />
            ),
            render: (r) => (
              <input
                type="checkbox"
                checked={selected.has(r.id)}
                onChange={(e) => { e.stopPropagation(); toggleRow(r.id); }}
                onClick={(e) => e.stopPropagation()}
                className="cursor-pointer"
                data-testid={`bulk-select-${r.id}`}
              />
            ),
          }] : []),
          { header: "Date", mono: true, render: (r) => r.date_received },
          { header: "Mode", render: (r) => {
            const fromProc = !!r.source_receiving_id;
            return (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-bold tracking-wider ${
                r.requires_processing
                  ? "bg-amber-100 text-amber-800"
                  : fromProc
                    ? "bg-emerald-100 text-emerald-900"
                    : "bg-emerald-50 text-emerald-800"
              }`} title={fromProc ? "From processing" : (r.requires_processing ? "Raw" : "Finished")}>
                {r.requires_processing ? "RAW" : fromProc ? "PROC" : "FIN"}
              </span>
            );
          }},
          { header: "Processed By", render: (r) => {
            const v = r.processed_by_us ?? (r.requires_processing || !!r.source_receiving_id);
            return (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-bold tracking-wider ${v ? "bg-teal-100 text-teal-900" : "bg-stone-100 text-stone-600"}`} data-testid={`pbu-${r.id}`}>
                {v ? "US" : "SUPPLIER"}{r.processed_by_us_qty ? ` (${r.processed_by_us_qty})` : ""}
              </span>
            );
          }},
          { header: "Product Type", render: (r) => r.product_type || "—" },
          { header: "Product", render: (r) => <span className="text-xs">{r.product_name || "—"}</span> },
          { header: "DN #", key: "delivery_note_number", mono: true },
          { header: "Order #", key: "order_number", mono: true },
          { header: "Destination", key: "export_destination" },
          { header: "Bundle #", key: "bundle_number", mono: true },
          { header: "Article #", key: "article_number", mono: true },
          { header: "WB #", key: "weighbridge_number", mono: true },
          { header: "Origin", key: "origin" },
          { header: "Transporter", render: (r) => <span className="text-xs">{r.transporter || "—"}</span> },
          { header: "Reg", key: "truck_registration", mono: true },
          { header: "Butt (mm)", align: "right", mono: true, render: (r) => r.butt_size != null ? fmtNumber(r.butt_size, 2) : "—" },
          { header: "Thick (mm)", align: "right", mono: true, render: (r) => r.thick ?? "—" },
          { header: "Wide (mm)", align: "right", mono: true, render: (r) => r.wide ?? "—" },
          { header: "Len (m)", align: "right", mono: true, render: (r) => r.length ?? "—" },
          { header: "Qty", mono: true, align: "right", render: (r) => (
            r.requires_processing && r.processed_quantity
              ? <span>{fmtNumber(effQty(r))} <span className="text-[10px] text-stone-400">of {fmtNumber(r.quantity)}</span></span>
              : fmtNumber(r.quantity)
          ) },
          { header: "Colour", render: (r) => r.colour || "—" },
          { header: "Incoming Total Weight", mono: true, align: "right", render: (r) => fmtNumber(effWeight(r)) },
          { header: "Avg Incoming Pole Weight", mono: true, align: "right", render: (r) => fmtNumber(r.avg_pole_weight, 3) },
          { header: "Calculated Weight (kg)", mono: true, align: "right", render: (r) => fmtNumber(calcWeight(r.butt_size, r.length, effQty(r))) },
          { header: "Status", render: (r) => <StatusBadge status={r.status} labels={RECEIVING_STATUS_LABELS} /> },
          { header: "Actions", align: "right", render: (r) => (
            <div className="flex items-center gap-1 justify-end">
              {canCreate && (
                <button onClick={(e) => { e.stopPropagation(); setEditing(r); }}
                        className="p-1 text-stone-600 hover:text-emerald-800" title="Edit" data-testid={`edit-${r.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
              {canCreate && r.status === "awaiting_paint" && (
                <>
                  <button onClick={(e) => { e.stopPropagation(); markPaintedLine(r); }}
                          className="p-1 text-stone-600 hover:text-fuchsia-700" title="Mark painted (Completed)" data-testid={`mark-painted-${r.id}`}>
                    <PaintBucket className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); revertLine(r); }}
                          className="p-1 text-stone-600 hover:text-amber-700" title="Send back to Processing" data-testid={`revert-${r.id}`}>
                    <RotateCcw className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
              {canDamage && isDamageable(r) && (
                <button onClick={(e) => { e.stopPropagation(); setDamageTarget(r); setDamageReason(""); }}
                        className="p-1 text-stone-600 hover:text-amber-700" title="Mark damaged" data-testid={`mark-damaged-${r.id}`}>
                  <AlertTriangle className="h-3.5 w-3.5" />
                </button>
              )}
              {canDelete && (
                <button onClick={(e) => { e.stopPropagation(); onDelete(r); }}
                        className="p-1 text-stone-600 hover:text-red-700" title="Delete" data-testid={`delete-${r.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )},
        ]}
        rows={pageRows}
      />
      )}
      {view === "lines" && <LinesPager />}

      {mode === "raw" && <RawModal onClose={() => { setMode(null); load(); }} />}
      {mode === "finished" && <FinishedModal onClose={() => { setMode(null); load(); }} />}
      {bulkPBU && (
        <BulkPBUModal
          lines={(selected.size ? filtered.filter((x) => selected.has(x.id)) : filtered).filter((x) => !x.source_receiving_id)}
          scopeLabel={selected.size ? `${selected.size} selected` : `all ${filtered.length} filtered`}
          onClose={() => setBulkPBU(false)}
          onDone={() => { setBulkPBU(false); load(); }}
        />
      )}
      {editing && (
        <EditModal record={editing} onClose={() => { setEditing(null); load(); }} />
      )}
      {selectedReceipt && (
        <ReceiptDetailModal
          group={selectedReceipt}
          onClose={() => { setReceiptSel(null); load(); }}
          onEditLine={(line) => { setReceiptSel(null); setEditing(line); }}
          onDeleteLine={onDelete}
          onDocsChanged={load}
        />
      )}

      {damageTarget && (
        <Modal open onClose={() => setDamageTarget(null)} title="Mark Pole as Damaged" size="md"
               testid="mark-damaged-modal"
               footer={<><Btn variant="secondary" onClick={() => setDamageTarget(null)}>Cancel</Btn>
                 <Btn variant="danger" onClick={doMarkDamaged} disabled={!damageReason.trim()} testid="mark-damaged-confirm">Mark Damaged</Btn></>}>
          <div className="text-sm text-stone-700 mb-3">
            Mark <span className="font-semibold">{damageTarget.delivery_note_number} · {damageTarget.bundle_number || damageTarget.product_name}</span> as damaged.
            {damageSalvageable === "yes"
              ? <> It will move to <span className="font-semibold">Processing → Awaiting Salvage</span> and drop out of available stock.</>
              : <> It will be <span className="font-semibold">written off</span> (not salvageable) and removed from inventory.</>}
          </div>
          <Field label="Salvageable?">
            <Select value={damageSalvageable} onChange={(e) => setDamageSalvageable(e.target.value)} data-testid="mark-damaged-salvageable">
              <option value="yes">Yes — send to Awaiting Salvage</option>
              <option value="no">No — write off (not salvageable)</option>
            </Select>
          </Field>
          <Field label="Reason" required>
            <Textarea rows={2} value={damageReason} onChange={(e) => setDamageReason(e.target.value)}
                      placeholder="e.g. Split end found during inspection" data-testid="mark-damaged-reason" />
          </Field>
        </Modal>
      )}
      {showBulkDelete && (
        <BulkDeleteModal
          items={selectedItems}
          onClose={() => setShowBulkDelete(false)}
          onDone={() => { setShowBulkDelete(false); load(); }}
        />
      )}
      {showBulkEdit && (
        <BulkEditModal
          items={selectedItems}
          onClose={() => setShowBulkEdit(false)}
          onDone={() => { setShowBulkEdit(false); setSelected(new Set()); load(); }}
        />
      )}
    </AppLayout>
  );
}

// ============ BULK EDIT MODAL ============
function BulkEditModal({ items, onClose, onDone }) {
  const [fields, setFields] = useState({ colour: "", butt_size: "", thick: "", wide: "", length: "" });
  const [saving, setSaving] = useState(false);
  const changed = Object.entries(fields).filter(([, v]) => `${v}`.trim() !== "");

  const apply = async () => {
    if (!changed.length) { toast.error("Fill in at least one field to change"); return; }
    setSaving(true);
    try {
      const r = await api.post("/receiving/bulk-edit", {
        ids: items.map((i) => i.id),
        fields: Object.fromEntries(changed),
      });
      const { updated, errors } = r.data;
      if (errors?.length) {
        toast.warning(`Updated ${updated} line(s); ${errors.length} failed: ${errors.slice(0, 3).join(" · ")}${errors.length > 3 ? " …" : ""}`, { duration: 9000 });
      } else {
        toast.success(`Updated ${updated} line(s)`);
      }
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const set = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Modal open onClose={onClose} title={`Bulk Edit ${items.length} Receipt Line(s)`} size="lg" testid="bulk-edit-modal"
      footer={<><Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={apply} disabled={saving || !changed.length} testid="bulk-edit-apply">
          {saving ? "Applying…" : `Apply to ${items.length} line(s)`}
        </Btn></>}>
      <div className="text-xs text-stone-500 mb-4">
        Only the fields you fill in are changed — blank fields keep each line's current value. Changes cascade to the
        linked stock items. If you change Thick or Wide and leave Butt Ø blank, Butt Ø is recomputed as (thick + wide) ÷ 2.
        Lines with dispatched stock are skipped and reported.
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Field label="Colour">
          <Input value={fields.colour} placeholder="keep unchanged" onChange={set("colour")} data-testid="bulk-edit-colour" />
        </Field>
        <Field label="Butt Ø (mm)">
          <Input type="number" step="1" value={fields.butt_size} placeholder="keep unchanged" onChange={set("butt_size")} data-testid="bulk-edit-butt_size" />
        </Field>
        <Field label="Thick (mm)">
          <Input type="number" step="1" value={fields.thick} placeholder="keep unchanged" onChange={set("thick")} data-testid="bulk-edit-thick" />
        </Field>
        <Field label="Wide (mm)">
          <Input type="number" step="1" value={fields.wide} placeholder="keep unchanged" onChange={set("wide")} data-testid="bulk-edit-wide" />
        </Field>
        <Field label="Length (m)">
          <Input type="number" step="0.1" value={fields.length} placeholder="keep unchanged" onChange={set("length")} data-testid="bulk-edit-length" />
        </Field>
      </div>
    </Modal>
  );
}

// ============ BULK DELETE MODAL ============
function BulkPBUModal({ lines, scopeLabel, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const yes = lines.filter((l) => (l.processed_by_us ?? l.requires_processing) === true).length;
  const apply = async (val) => {
    setSaving(true);
    try {
      const r = await api.post("/receiving/bulk-processed-by-us", { ids: lines.map((l) => l.id), processed_by_us: val });
      toast.success(`${r.data.updated} receipt line(s) marked ${val ? "processed by us" : "NOT processed by us"}`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} title="Bulk — Processed By Us" size="sm" testid="bulk-pbu-modal"
      footer={<>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn variant="secondary" onClick={() => apply(false)} disabled={saving || !lines.length} testid="bulk-pbu-no">
          Mark NOT Processed By Us
        </Btn>
        <Btn onClick={() => apply(true)} disabled={saving || !lines.length} testid="bulk-pbu-yes">
          Mark Processed By Us
        </Btn>
      </>}>
      <div className="text-sm text-stone-700" data-testid="bulk-pbu-scope">
        Applies to <b>{lines.length}</b> receipt line(s) — {scopeLabel}. Lines created by the processing module are
        excluded; their flag lives on the original raw receipt.
      </div>
      <div className="text-xs text-stone-500 mt-2">
        Currently: {yes} marked Yes · {lines.length - yes} marked No. Any partial quantities on these lines are cleared.
      </div>
      <div className="text-xs text-stone-500 mt-1">
        Tip: use the search and filters first, then tick specific lines — or leave nothing ticked to apply to everything
        that matches the filters.
      </div>
    </Modal>
  );
}


function BulkDeleteModal({ items, onClose, onDone }) {
  const [force, setForce] = useState(false);
  const [working, setWorking] = useState(false);
  const ids = items.map((r) => r.id);
  const stats = items.reduce((a, r) => {
    a.total_qty += Number(r.quantity) || 0;
    if (r.requires_processing) a.raw += 1;
    else if (r.source_receiving_id) a.proc += 1;
    else a.fin += 1;
    return a;
  }, { raw: 0, fin: 0, proc: 0, total_qty: 0 });

  const submit = async () => {
    setWorking(true);
    try {
      const r = await api.post("/receiving/bulk-delete", { ids, force });
      const s = r.data;
      toast.success(
        `Deleted ${s.receipts_deleted} receipts (+${s.child_receipts_deleted} processed children, ${s.stock_items_deleted} stock items)`,
      );
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setWorking(false); }
  };

  return (
    <Modal
      open onClose={onClose}
      title={`Delete ${items.length} receipt${items.length === 1 ? "" : "s"}?`}
      testid="bulk-delete-modal"
      footer={<>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn variant="danger" onClick={submit} disabled={working} testid="bulk-delete-confirm">
          {working ? "Deleting..." : `Delete ${items.length}`}
        </Btn>
      </>}
    >
      <div className="space-y-3 text-sm">
        <div className="grid grid-cols-3 gap-3">
          <div className="panel p-3">
            <div className="data-label">RAW</div>
            <div className="text-xl font-display font-bold">{stats.raw}</div>
          </div>
          <div className="panel p-3">
            <div className="data-label">Finished</div>
            <div className="text-xl font-display font-bold">{stats.fin}</div>
          </div>
          <div className="panel p-3">
            <div className="data-label">From Processing</div>
            <div className="text-xl font-display font-bold">{stats.proc}</div>
          </div>
        </div>
        <div className="text-stone-700">
          Total poles affected: <span className="font-mono font-bold">{stats.total_qty}</span>
        </div>
        <div className="text-xs bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm">
          This will cascade-delete linked stock_items, processing records, damages,
          and any child receipts created by processing. Documents will be soft-deleted.
          Already-dispatched stock will block the operation.
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input
            type="checkbox"
            checked={force}
            onChange={(e) => setForce(e.target.checked)}
            data-testid="bulk-delete-force"
          />
          <span>Force-delete even if some stock is allocated to orders (will un-allocate)</span>
        </label>
        <details className="text-xs text-stone-600">
          <summary className="cursor-pointer">Show DNs to delete ({items.length})</summary>
          <ul className="mt-2 max-h-40 overflow-auto font-mono">
            {items.slice(0, 100).map((r) => (
              <li key={r.id}>· {r.delivery_note_number || r.id.slice(0, 8)} (qty {r.quantity})</li>
            ))}
            {items.length > 100 && <li className="text-stone-400">… and {items.length - 100} more</li>}
          </ul>
        </details>
      </div>
    </Modal>
  );
}

// ============ RAW MODAL ============
function RawModal({ onClose }) {
  const [form, setForm] = useState(rawEmpty);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
  }, []);

  const avgPole = (Number(form.weight) || 0) && (Number(form.quantity) || 0)
    ? (Number(form.weight) / Number(form.quantity)).toFixed(3) : "";

  const save = async () => {
    setSaving(true);
    try {
      const p = { ...form };
      ["quantity", "weight"].forEach((k) => p[k] = Number(p[k] || 0));
      p.avg_pole_weight = avgPole ? Number(avgPole) : null;
      p.requires_processing = true;
      p.processed_by_us = form.processed_by_us !== false;
      p.processed_by_us_qty = form.processed_by_us_qty === "" || form.processed_by_us_qty == null ? null : Number(form.processed_by_us_qty);
      await api.post("/receiving", p);
      toast.success("Raw receiving captured");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title="Receive RAW — poles to be processed" size="lg" testid="raw-modal"
           footer={<><Btn variant="secondary" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={saving} testid="raw-save">{saving ? "Saving..." : "Save Receiving"}</Btn></>}>
      <div className="text-xs text-stone-500 mb-4">
        Use this when poles arrive unprocessed and must go through the processing step. They land in stock as <b>pending processing</b>.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Date Received" required><Input type="date" value={form.date_received} onChange={(e) => setForm({ ...form, date_received: e.target.value })} /></Field>
        <Field label="Product Type" required><Select value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })}>{PRODUCT_TYPES.map((p) => <option key={p}>{p}</option>)}</Select></Field>
        <Field label="Product (optional)">
          <Select value={form.product_id} onChange={(e) => { const p = products.find((x) => x.id === e.target.value); setForm({ ...form, product_id: e.target.value, product_name: p?.name || "" }); }}>
            <option value="">—</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        </Field>
        <Field label="Delivery Note Number" required><Input value={form.delivery_note_number} onChange={(e) => setForm({ ...form, delivery_note_number: e.target.value })} /></Field>
        <Field label="Weighbridge Number"><Input value={form.weighbridge_number} onChange={(e) => setForm({ ...form, weighbridge_number: e.target.value })} data-testid="raw-wb" /><WbDupWarning value={form.weighbridge_number} /></Field>
        <Field label="Origin" required>
          <Select value={form.origin} onChange={(e) => { const sup = suppliers.find((s) => s.origin === e.target.value); setForm({ ...form, origin: e.target.value, supplier_id: sup?.id || "", supplier_name: sup?.name || "" }); }}>{ORIGINS.map((o) => <option key={o}>{o}</option>)}</Select>
        </Field>
        <Field label="Transporter" required><Input value={form.transporter} onChange={(e) => setForm({ ...form, transporter: e.target.value })} /></Field>
        <Field label="Truck Registration" required><Input value={form.truck_registration} onChange={(e) => setForm({ ...form, truck_registration: e.target.value })} /></Field>
        <Field label="Quantity (pcs)" required><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></Field>
        <Field label="Incoming Total Weight (kg)"><Input type="number" step="0.01" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} /></Field>
        <Field label="Avg Incoming Pole Weight (auto)"><Input value={avgPole} readOnly className="bg-stone-50" /></Field>
        <Field label="Processed by us?">
          <Select value={form.processed_by_us === false ? "no" : "yes"}
                  onChange={(e) => setForm({ ...form, processed_by_us: e.target.value === "yes" })} data-testid="raw-processed-by-us">
            <option value="yes">Yes — we process these poles</option>
            <option value="no">No — processed elsewhere</option>
          </Select>
        </Field>
        {form.processed_by_us !== false && (
          <Field label="Poles processed by us (blank = all)">
            <Input type="number" value={form.processed_by_us_qty ?? ""} onChange={(e) => setForm({ ...form, processed_by_us_qty: e.target.value })} data-testid="raw-processed-qty" />
          </Field>
        )}
        <div className="md:col-span-3"><Field label="Comments"><Textarea rows={2} value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} /></Field></div>
      </div>
    </Modal>
  );
}

// ============ FINISHED MODAL ============
function FinishedModal({ onClose }) {
  const [tab, setTab] = useState("single");
  return (
    <Modal open onClose={onClose} title="Receive FINISHED — already-processed poles" size="xl" testid="finished-modal">
      <div className="flex border-b border-stone-200 mb-4">
        {[
          { id: "single", label: "Single Entry" },
          { id: "paste", label: "Paste from Excel" },
          { id: "upload", label: "Upload File" },
        ].map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium ${tab === t.id ? "border-b-2 border-emerald-800 text-emerald-900" : "text-stone-500 hover:text-stone-800"}`}
            data-testid={`tab-${t.id}`}>{t.label}</button>
        ))}
        <a href={`${API}/receiving/template.csv`} target="_blank" rel="noreferrer"
           className="ml-auto px-3 py-2 text-xs text-emerald-800 hover:underline flex items-center gap-1" data-testid="download-template">
          <FileSpreadsheet className="h-3.5 w-3.5" /> Download CSV template
        </a>
      </div>
      {tab === "single" && <FinishedFormFields initial={finishedEmpty} onSubmitted={onClose} mode="create" />}
      {tab === "paste" && <PasteImport onDone={onClose} />}
      {tab === "upload" && <FileImport onDone={onClose} />}
    </Modal>
  );
}

// ============ EDIT MODAL (full fields, pre-filled) ============
function EditModal({ record, onClose }) {
  // Pre-fill exclusively from the record. Use empty strings for missing values
  // so the user can type into them and they don't show stale defaults.
  const initial = {
    ...finishedEmpty,
    ...record,
    product_id: record.product_id ?? "",
    product_name: record.product_name ?? "",
    product_type: record.product_type ?? "Poles",
    delivery_note_number: record.delivery_note_number ?? "",
    order_number: record.order_number ?? "",
    export_destination: record.export_destination ?? "",
    bundle_number: record.bundle_number ?? "",
    article_number: record.article_number ?? "",
    weighbridge_number: record.weighbridge_number ?? "",
    origin: record.origin ?? "EIT",
    transporter: record.transporter ?? "",
    truck_registration: record.truck_registration ?? "",
    butt_size: record.butt_size ?? "",
    thick: record.thick ?? "",
    wide: record.wide ?? "",
    length: record.length ?? "",
    colour: record.colour ?? "",
    comments: record.comments ?? "",
    processed_by_us: record.processed_by_us ?? (record.requires_processing ? true : false),
    processed_by_us_qty: record.processed_by_us_qty ?? "",
  };
  return (
    <Modal open onClose={onClose}
           title={`Edit Receipt · ${record.delivery_note_number || record.id.slice(0, 8)}`}
           size="xl" testid="edit-modal">
      <FinishedFormFields initial={initial} onSubmitted={onClose} mode="edit" recordId={record.id} />
    </Modal>
  );
}

// ============ Shared form fields (used by Finished Single Entry AND Edit) ============
function FinishedFormFields({ initial, onSubmitted, mode, recordId }) {
  const [form, setForm] = useState(initial);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
  }, []);

  const avgPole = (Number(form.weight) || 0) && (Number(form.quantity) || 0)
    ? (Number(form.weight) / Number(form.quantity)).toFixed(3) : "";

  // Butt size = (thick + wide) / 2, in mm. Read-only computed.
  const computedButt = (Number(form.thick) || 0) && (Number(form.wide) || 0)
    ? ((Number(form.thick) + Number(form.wide)) / 2).toFixed(2) : "";

  const save = async () => {
    setSaving(true);
    try {
      const p = { ...form };
      ["quantity", "weight", "thick", "wide", "length", "butt_size"].forEach((k) => {
        if (p[k] === "" || p[k] == null) p[k] = null;
        else p[k] = Number(p[k]);
      });
      p.quantity = Number(p.quantity || 0);
      p.weight = Number(p.weight || 0);
      p.avg_pole_weight = avgPole ? Number(avgPole) : null;
      // Always send the computed butt_size (or null if both thick/wide missing)
      p.butt_size = computedButt ? Number(computedButt) : null;
      p.processed_by_us = form.processed_by_us === true;
      p.processed_by_us_qty = form.processed_by_us_qty === "" || form.processed_by_us_qty == null ? null : Number(form.processed_by_us_qty);
      if (mode === "edit") {
        await api.patch(`/receiving/${recordId}`, p);
        toast.success("Receipt updated");
      } else {
        p.requires_processing = false;
        await api.post("/receiving", p);
        toast.success("Finished receiving captured");
      }
      onSubmitted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Date Received" required><Input type="date" value={form.date_received} onChange={(e) => setForm({ ...form, date_received: e.target.value })} data-testid="ff-date" /></Field>
        <Field label="Product Type" required><Select value={form.product_type || "Poles"} onChange={(e) => setForm({ ...form, product_type: e.target.value })}>{PRODUCT_TYPES.map((p) => <option key={p}>{p}</option>)}</Select></Field>
        <Field label="Product (optional)">
          <Select value={form.product_id || ""} onChange={(e) => { const p = products.find((x) => x.id === e.target.value); setForm({ ...form, product_id: e.target.value, product_name: p?.name || form.product_name }); }}>
            <option value="">—</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        </Field>

        <Field label="Delivery Note #" required><Input value={form.delivery_note_number || ""} onChange={(e) => setForm({ ...form, delivery_note_number: e.target.value })} data-testid="ff-dn" /></Field>
        <Field label="Order Number"><Input value={form.order_number || ""} onChange={(e) => setForm({ ...form, order_number: e.target.value })} data-testid="ff-order" /></Field>
        <Field label="Export Destination"><Input value={form.export_destination || ""} onChange={(e) => setForm({ ...form, export_destination: e.target.value })} /></Field>

        <Field label="Bundle / Pole Number"><Input value={form.bundle_number || ""} onChange={(e) => setForm({ ...form, bundle_number: e.target.value })} data-testid="ff-bundle" /></Field>
        <Field label="Article Number"><Input value={form.article_number || ""} onChange={(e) => setForm({ ...form, article_number: e.target.value })} /></Field>
        <Field label="Weighbridge Number"><Input value={form.weighbridge_number || ""} onChange={(e) => setForm({ ...form, weighbridge_number: e.target.value })} data-testid="ff-wb" /><WbDupWarning value={form.weighbridge_number} /></Field>

        <Field label="Origin" required><Select value={form.origin || "EIT"} onChange={(e) => setForm({ ...form, origin: e.target.value })}>{ORIGINS.map((o) => <option key={o}>{o}</option>)}</Select></Field>
        <Field label="Transporter"><Input value={form.transporter || ""} onChange={(e) => setForm({ ...form, transporter: e.target.value })} /></Field>
        <Field label="Truck Registration"><Input value={form.truck_registration || ""} onChange={(e) => setForm({ ...form, truck_registration: e.target.value })} /></Field>

        <Field label="Thick (mm)"><Input type="number" step="0.1" value={form.thick} onChange={(e) => setForm({ ...form, thick: e.target.value })} data-testid="ff-thick" /></Field>
        <Field label="Wide (mm)"><Input type="number" step="0.1" value={form.wide} onChange={(e) => setForm({ ...form, wide: e.target.value })} data-testid="ff-wide" /></Field>
        <Field label="Butt Size (mm) — auto (thick+wide)/2">
          <Input value={computedButt} readOnly className="bg-stone-50" data-testid="ff-butt" />
        </Field>

        <Field label="Length (m)">
          <LengthInput value={form.length} onChange={(v) => setForm({ ...form, length: v })} presets={LENGTHS} listId="rec-len-presets" />
        </Field>
        <Field label="Butt Colour">
          <Select value={form.colour || ""} onChange={(e) => setForm({ ...form, colour: e.target.value })}>
            <option value="">—</option>
            {COLOURS.map((c) => <option key={c}>{c}</option>)}
          </Select>
        </Field>
        <Field label="Quantity (pcs)" required><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="ff-qty" /></Field>

        <Field label="Incoming Total Weight (kg)"><Input type="number" step="0.01" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} /></Field>
        <Field label="Avg Incoming Pole Weight (auto)"><Input value={avgPole} readOnly className="bg-stone-50" /></Field>
        <Field label="Processed by us?">
          <Select value={form.processed_by_us === true ? "yes" : "no"}
                  onChange={(e) => setForm({ ...form, processed_by_us: e.target.value === "yes" })} data-testid="ff-processed-by-us">
            <option value="no">No — received already processed</option>
            <option value="yes">Yes — we processed these poles</option>
          </Select>
        </Field>
        {form.processed_by_us === true && (
          <Field label="Poles processed by us (blank = all)">
            <Input type="number" value={form.processed_by_us_qty ?? ""} onChange={(e) => setForm({ ...form, processed_by_us_qty: e.target.value })} data-testid="ff-processed-qty" />
          </Field>
        )}

        <div className="md:col-span-3"><Field label="Comments"><Textarea rows={2} value={form.comments || ""} onChange={(e) => setForm({ ...form, comments: e.target.value })} /></Field></div>
      </div>
      <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-stone-200">
        <Btn onClick={save} disabled={saving} testid="ff-save">
          {saving ? "Saving..." : mode === "edit" ? "Save Changes" : "Save Receiving"}
        </Btn>
      </div>
    </div>
  );
}

// ============ DOCS MODAL ============
const DOC_CATEGORIES = [
  "delivery_note", "weighbridge_slip", "photo", "transport_doc",
  "container_doc", "proof_of_delivery", "other",
];

function DocsPanel({ linkedTo, linkedId, onChanged }) {
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("delivery_note");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const r = await api.get(`/documents`, { params: { linked_to: linkedTo, linked_id: linkedId } });
    setDocs(r.data);
  };
  useEffect(() => { load(); }, [linkedId]); // eslint-disable-line

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", category);
        fd.append("linked_to", linkedTo);
        fd.append("linked_id", linkedId);
        await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} file(s) uploaded`);
      load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDeleteDoc = async (id) => {
    if (!confirm("Remove this document?")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Removed");
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div>
      <div className="flex items-end gap-2 mb-3 pb-3 border-b border-stone-200">
        <Field label="Category">
          <Select value={category} onChange={(e) => setCategory(e.target.value)} className="min-w-[200px]" data-testid="docs-category">
            {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </Select>
        </Field>
        <input ref={fileRef} type="file" multiple onChange={onUpload} className="hidden" data-testid="docs-file-input" />
        <Btn onClick={() => fileRef.current?.click()} disabled={uploading} testid="docs-upload-btn">
          <Upload className="h-4 w-4" /> {uploading ? "Uploading..." : "Upload"}
        </Btn>
      </div>

      <div className="space-y-1 max-h-64 overflow-y-auto">
        {docs.length === 0 && (
          <div className="text-stone-400 text-sm py-6 text-center">No documents yet for this receipt.</div>
        )}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center gap-3 px-3 py-2 hover:bg-stone-50 border-b border-stone-100 text-sm">
            <FileText className="h-4 w-4 text-stone-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{d.original_filename}</div>
              <div className="text-xs text-stone-500">
                {d.category?.replace(/_/g, " ")} · {Math.round((d.size || 0) / 1024)} KB · {fmtDate(d.created_at)}
              </div>
            </div>
            <a href={`${API}/documents/${d.id}/download`} target="_blank" rel="noreferrer"
               className="text-emerald-800 hover:text-emerald-900 px-2 py-1" data-testid={`doc-dl-${d.id}`}>
              <Download className="h-4 w-4" />
            </a>
            <button onClick={() => onDeleteDoc(d.id)} className="text-stone-500 hover:text-red-700 px-2 py-1"
                    data-testid={`doc-del-${d.id}`} title="Remove">
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ RECEIPT DETAIL MODAL (breakdown drill-in) ============
function ReceiptDetailModal({ group, onClose, onEditLine, onDeleteLine, onDocsChanged }) {
  const { user } = useAuth();
  const canCreate = can(user?.role, "receiving:create");
  const canDelete = can(user?.role, "receiving:delete");
  const lines = group.lines;
  const first = lines[0] || {};
  const totalQty = lines.reduce((a, r) => a + (Number(r.quantity) || 0), 0);
  const totalWeight = lines.reduce((a, r) => a + (Number(r.weight) || 0), 0);

  return (
    <Modal open onClose={onClose}
           title={`Receipt · ${first.delivery_note_number || "—"} · ${first.date_received || ""}`}
           size="xl" testid="receipt-detail-modal"
           footer={<Btn variant="secondary" onClick={onClose}>Close</Btn>}>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm mb-5">
        <div><div className="data-label">DN</div><div className="font-mono">{first.delivery_note_number || "—"}</div></div>
        <div><div className="data-label">Date</div><div className="font-mono">{first.date_received || "—"}</div></div>
        <div><div className="data-label">Origin</div><div>{first.origin || "—"}</div></div>
        <div><div className="data-label">Lines</div><div className="font-mono">{lines.length}</div></div>
        <div><div className="data-label">Totals</div><div className="font-mono">{fmtNumber(totalQty)} pcs · {fmtNumber(totalWeight)} kg</div></div>
      </div>

      <div className="mb-6">
        <div className="font-display text-base font-semibold mb-2">Receipt Documents</div>
        <div className="text-xs text-stone-500 mb-2">Upload delivery notes, weighbridge slips, photos and transport docs once for the whole receipt.</div>
        <DocsPanel linkedTo="receipt" linkedId={group.key} onChanged={onDocsChanged} />
      </div>

      <div className="font-display text-base font-semibold mb-2">Line Items ({lines.length})</div>
      <div className="panel max-h-96 overflow-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-stone-100 border-b sticky top-0">
            <tr>
              {["Mode", "Proc By", "Product", "Bundle", "Article", "Butt", "Thick", "Wide", "Len", "Qty", "Colour", "Weight", "Status", ""].map((h) => (
                <th key={h} className="px-2 py-1.5 text-left font-semibold text-stone-700 uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.map((r) => {
              const m = r.requires_processing ? "RAW" : r.source_receiving_id ? "PROC" : "FIN";
              return (
                <tr key={r.id} className="border-b hover:bg-stone-50" data-testid={`receipt-line-${r.id}`}>
                  <td className="px-2 py-1"><span className="text-[10px] font-bold tracking-wider">{m}</span></td>
                  <td className="px-2 py-1"><span className="text-[10px] font-bold tracking-wider">{(r.processed_by_us ?? (r.requires_processing || !!r.source_receiving_id)) ? "US" : "SUPPLIER"}</span></td>
                  <td className="px-2 py-1">{r.product_name || "—"}</td>
                  <td className="px-2 py-1 font-mono">{r.bundle_number || "—"}</td>
                  <td className="px-2 py-1 font-mono">{r.article_number || "—"}</td>
                  <td className="px-2 py-1 font-mono text-right">{r.butt_size != null ? fmtNumber(r.butt_size, 2) : "—"}</td>
                  <td className="px-2 py-1 font-mono text-right">{r.thick ?? "—"}</td>
                  <td className="px-2 py-1 font-mono text-right">{r.wide ?? "—"}</td>
                  <td className="px-2 py-1 font-mono text-right">{r.length ?? "—"}</td>
                  <td className="px-2 py-1 font-mono text-right">{fmtNumber(r.quantity)}</td>
                  <td className="px-2 py-1">{r.colour || "—"}</td>
                  <td className="px-2 py-1 font-mono text-right">{fmtNumber(r.weight)}</td>
                  <td className="px-2 py-1"><StatusBadge status={r.status} labels={RECEIVING_STATUS_LABELS} /></td>
                  <td className="px-2 py-1">
                    <div className="flex gap-1">
                      {canCreate && (
                        <button onClick={() => onEditLine(r)} className="text-stone-600 hover:text-emerald-800" title="Edit" data-testid={`receipt-line-edit-${r.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => onDeleteLine(r)} className="text-stone-600 hover:text-red-700" title="Delete" data-testid={`receipt-line-delete-${r.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

// ============ Bulk paste + upload preview ============
function BulkPreview({ rows, setRows, onCommit, committing }) {
  const [wbWeight, setWbWeight] = useState("");
  if (rows.length === 0) return null;
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const editCell = (i, key, val) => { const c = [...rows]; c[i] = { ...c[i], [key]: val }; setRows(c); };

  const totalQty = rows.reduce((a, r) => a + (Number(r.quantity) || 0), 0);
  const applyWb = () => {
    const wb = Number(wbWeight);
    if (!wb || wb <= 0) { toast.error("Enter a positive weighbridge weight"); return; }
    if (totalQty <= 0) { toast.error("No quantities to distribute against"); return; }
    const avg = wb / totalQty;
    const next = rows.map((r) => {
      const q = Number(r.quantity) || 0;
      return { ...r, weight: q > 0 ? Number((avg * q).toFixed(2)) : r.weight };
    });
    setRows(next);
    toast.success(`Distributed ${wb} kg across ${rows.length} rows (avg ${avg.toFixed(3)} kg/pole)`);
  };

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-end gap-3 mb-3 pb-3 border-b border-stone-200">
        <div>
          <div className="data-label mb-1">Weighbridge Weight (kg) — optional</div>
          <div className="flex gap-2 items-center">
            <Input type="number" step="0.01" value={wbWeight}
                   onChange={(e) => setWbWeight(e.target.value)}
                   placeholder="e.g. 9800"
                   className="w-40"
                   data-testid="bulk-wb-weight" />
            <Btn variant="secondary" onClick={applyWb} testid="bulk-wb-apply">Apply to all rows</Btn>
            <span className="text-xs text-stone-500">
              {totalQty > 0 ? `Total qty: ${totalQty} pcs` : "—"}
            </span>
          </div>
          <div className="text-[11px] text-stone-500 mt-1">
            Distributes the truck weight proportionally to each row&apos;s quantity (avg pole weight is auto-derived).
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="data-label">{rows.length} rows to import — review and edit</div>
          <Btn onClick={onCommit} disabled={committing} testid="bulk-commit">
            {committing ? "Importing..." : `Import ${rows.length} rows`}
          </Btn>
        </div>
      </div>
      <div className="panel max-h-96 overflow-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-stone-100 border-b sticky top-0">
            <tr>
              {["Date", "Product", "DN", "Order", "Dest", "Bundle", "Article", "WB#", "Origin", "Transp", "Reg", "Butt", "Len", "Qty", "Colour", "Weight", "Proc By Us", "Bundled", ""].map((h) => (
                <th key={h} className="px-2 py-1.5 text-left font-semibold text-stone-700 uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b hover:bg-stone-50">
                <td className="px-1"><input className="w-24 px-1 py-0.5 border-0 font-mono text-xs" value={r.date_received || ""} onChange={(e) => editCell(i, "date_received", e.target.value)} /></td>
                <td className="px-1"><input className="w-28 px-1 py-0.5 border-0 text-xs" value={r.product_name || ""} onChange={(e) => editCell(i, "product_name", e.target.value)} /></td>
                <td className="px-1"><input className="w-20 px-1 py-0.5 border-0 font-mono text-xs" value={r.delivery_note_number || ""} onChange={(e) => editCell(i, "delivery_note_number", e.target.value)} /></td>
                <td className="px-1"><input className="w-24 px-1 py-0.5 border-0 font-mono text-xs" value={r.order_number || ""} onChange={(e) => editCell(i, "order_number", e.target.value)} /></td>
                <td className="px-1"><input className="w-20 px-1 py-0.5 border-0 text-xs" value={r.export_destination || ""} onChange={(e) => editCell(i, "export_destination", e.target.value)} /></td>
                <td className="px-1"><input className="w-20 px-1 py-0.5 border-0 font-mono text-xs" value={r.bundle_number || ""} onChange={(e) => editCell(i, "bundle_number", e.target.value)} /></td>
                <td className="px-1"><input className="w-24 px-1 py-0.5 border-0 font-mono text-xs" value={r.article_number || ""} onChange={(e) => editCell(i, "article_number", e.target.value)} /></td>
                <td className="px-1"><input className="w-16 px-1 py-0.5 border-0 font-mono text-xs" value={r.weighbridge_number || ""} onChange={(e) => editCell(i, "weighbridge_number", e.target.value)} /></td>
                <td className="px-1"><input className="w-14 px-1 py-0.5 border-0 text-xs" value={r.origin || ""} onChange={(e) => editCell(i, "origin", e.target.value)} /></td>
                <td className="px-1"><input className="w-20 px-1 py-0.5 border-0 text-xs" value={r.transporter || ""} onChange={(e) => editCell(i, "transporter", e.target.value)} /></td>
                <td className="px-1"><input className="w-16 px-1 py-0.5 border-0 font-mono text-xs" value={r.truck_registration || ""} onChange={(e) => editCell(i, "truck_registration", e.target.value)} /></td>
                <td className="px-1"><input className="w-14 px-1 py-0.5 border-0 font-mono text-xs" value={r.butt_size || ""} onChange={(e) => editCell(i, "butt_size", e.target.value)} /></td>
                <td className="px-1"><input className="w-12 px-1 py-0.5 border-0 font-mono text-xs" value={r.length ?? ""} onChange={(e) => editCell(i, "length", e.target.value)} /></td>
                <td className="px-1"><input className="w-14 px-1 py-0.5 border-0 font-mono text-xs" value={r.quantity ?? ""} onChange={(e) => editCell(i, "quantity", e.target.value)} /></td>
                <td className="px-1"><input className="w-14 px-1 py-0.5 border-0 text-xs" value={r.colour || ""} onChange={(e) => editCell(i, "colour", e.target.value)} /></td>
                <td className="px-1"><input className="w-16 px-1 py-0.5 border-0 font-mono text-xs" value={r.weight ?? ""} onChange={(e) => editCell(i, "weight", e.target.value)} /></td>
                <td className="px-1">
                  <select className="px-1 py-0.5 border-0 text-xs bg-transparent" value={r.processed_by_us ? "yes" : "no"}
                          onChange={(e) => editCell(i, "processed_by_us", e.target.value === "yes")}
                          data-testid={`bulk-preview-pbu-${i}`}>
                    <option value="no">Supplier</option>
                    <option value="yes">Us</option>
                  </select>
                </td>
                <td className="px-1">
                  <select className="px-1 py-0.5 border-0 text-xs bg-transparent" value={r.bundled ? "yes" : "no"}
                          onChange={(e) => editCell(i, "bundled", e.target.value === "yes")}
                          data-testid={`bulk-preview-bundled-${i}`}>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </td>
                <td className="px-1"><button onClick={() => removeRow(i)} className="text-red-600 hover:text-red-800"><Trash2 className="h-3.5 w-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function cleanBulkRows(rows) {
  return rows.map((r) => {
    const out = { ...r, requires_processing: false, is_damaged: !!r.is_damaged };
    ["quantity", "weight", "thick", "wide", "length"].forEach((k) => {
      if (out[k] === "" || out[k] == null) out[k] = null;
      else out[k] = Number(out[k]);
    });
    out.quantity = Number(out.quantity || 0);
    out.weight = Number(out.weight || 0);
    return out;
  });
}

function PasteImport({ onDone }) {
  const [text, setText] = useState("");
  const [rows, setRows] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);

  const parse = async () => {
    if (!text.trim()) { toast.error("Paste something first"); return; }
    setParsing(true);
    try {
      const r = await api.post("/receiving/parse-text", { text });
      setRows(r.data.rows);
      if (r.data.row_count === 0) toast.error("No rows detected. Make sure the first line is a header row.");
      else toast.success(`Parsed ${r.data.row_count} rows`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setParsing(false); }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const res = await api.post("/receiving/bulk", { rows: cleanBulkRows(rows) });
      if (res.data.errors?.length) {
        toast.error(`Imported ${res.data.created_count}, ${res.data.errors.length} errors`);
      } else {
        toast.success(`Imported ${res.data.created_count} rows`);
      }
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setCommitting(false); }
  };

  return (
    <div>
      <div className="text-xs text-stone-500 mb-2">
        Copy a range of cells from Excel (including the header row) and paste below. The system auto-detects tab/comma delimiters and maps common column names like &quot;Date Received&quot;, &quot;DN&quot;, &quot;Pole Number&quot;, &quot;Butt Colour&quot; etc.
      </div>
      <Textarea rows={6}
        placeholder="Paste your Excel range here. First row should be headers (e.g. Date Received, Product, DN, Bundle Number, Quantity, Colour...)"
        value={text} onChange={(e) => setText(e.target.value)} data-testid="paste-textarea" />
      <div className="flex justify-end mt-2">
        <Btn onClick={parse} disabled={parsing} testid="parse-btn">
          <ClipboardPaste className="h-4 w-4" /> {parsing ? "Parsing..." : "Parse Pasted Data"}
        </Btn>
      </div>
      <BulkPreview rows={rows} setRows={setRows} onCommit={commit} committing={committing} />
    </div>
  );
}

function FileImport({ onDone }) {
  const fileRef = useRef();
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/receiving/upload-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRows(r.data.rows);
      toast.success(`Parsed ${r.data.row_count} rows from ${f.name}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const res = await api.post("/receiving/bulk", { rows: cleanBulkRows(rows) });
      toast.success(`Imported ${res.data.created_count} rows`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setCommitting(false); }
  };

  return (
    <div>
      <div className="text-xs text-stone-500 mb-3">
        Upload a CSV or Excel (.xlsx) file. Use the template above for the expected column layout. Common column-name variations are auto-mapped.
      </div>
      <div className="panel p-8 border-dashed border-2 text-center">
        <Upload className="h-8 w-8 text-stone-400 mx-auto mb-2" />
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFile} className="hidden" data-testid="file-input" />
        <Btn variant="secondary" onClick={() => fileRef.current?.click()} disabled={parsing} testid="select-file-btn">
          {parsing ? "Parsing..." : "Choose file"}
        </Btn>
        {fileName && <div className="mt-2 text-xs text-stone-600 font-mono">{fileName}</div>}
      </div>
      <BulkPreview rows={rows} setRows={setRows} onCommit={commit} committing={committing} />
    </div>
  );
}
